import {
  activateaccount,
  createAccount,
  deleteAccount,
  getAccount,
  updateAccount,
  updateCallPreference,
} from "@shared/api/account";
import { keyFactory } from "@shared/api/hooks/utils";
import { useDatadogUser } from "@shared/datadog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  Account,
  DeleteAccountFormData,
  CallPreferencesPayload,
} from "@shared/constants";

const accountKeys = keyFactory("account");

export const useCreateAccount = () =>
  useMutation({
    mutationFn: async (newAccountInfo: unknown) =>
      createAccount(newAccountInfo),
  });

const callPreferenceKeys = keyFactory("call-preference");

export const useGetAccount = <TResult extends Account = Account>(
  select?: (data: Account) => TResult,
  enabled?: boolean
) => {
  const account = useQuery({
    queryKey: accountKeys.detail(undefined),
    queryFn: getAccount,
    select,
    enabled,
  });

  // Only send non-identifying identifiers to Datadog RUM. PII fields such as
  // name/email must never be passed to setUser (see OG-25181 / INC-1833).
  useDatadogUser(
    account.isSuccess
      ? {
          id: `${account.data.id}`,
          rid: account.data.rid,
        }
      : undefined
  );

  return account;
};

export const useMutateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newAccountInfo: Account) =>
      updateAccount(newAccountInfo),
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: accountKeys.detail(undefined),
      });
    },
    retry: 3,
  });
};

export const useDeleteAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (feedback: DeleteAccountFormData) =>
      deleteAccount(feedback),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: accountKeys.detail(undefined),
      });
    },
    retry: 2,
  });
};

export const useMutateCallPreference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (preference: CallPreferencesPayload) =>
      updateCallPreference(preference),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: callPreferenceKeys.detail(undefined),
      });
    },
    retry: 2,
  });
};

export const useActivateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      params,
    }: {
      accountId: number;
      params: unknown;
    }) => activateaccount(accountId, params),
    onError: (error) => {
      console.log("activateAccount error", error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: accountKeys.detail(undefined),
      });
    },
    // NO RETRY
    // if a user comes back to the page after a while
    // or the account is already activated the BE return a 422
    retry: 0,
  });
};
